// src/utils/dateUtils.js

/**
 * Format a date string into a human-readable format
 * @param {string} dateString - ISO date string to format
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    // Format the date (e.g., "Mar 15, 2023")
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Error formatting date';
  }
};

/**
 * Get time remaining until a future date
 * @param {string} futureDate - ISO date string of future date
 * @returns {Object} Object containing days, hours, minutes remaining
 */
export const getTimeRemaining = (futureDate) => {
  if (!futureDate) return { days: 0, hours: 0, minutes: 0 };
  
  try {
    const future = new Date(futureDate).getTime();
    const now = new Date().getTime();
    
    // If date is in the past
    if (future <= now) {
      return { days: 0, hours: 0, minutes: 0 };
    }
    
    const difference = future - now;
    
    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
    };
  } catch (error) {
    console.error('Error calculating time remaining:', error);
    return { days: 0, hours: 0, minutes: 0 };
  }
};

/**
 * Check if a date is in the past
 * @param {string} dateString - ISO date string to check
 * @returns {boolean} True if date is in the past
 */
export const isDatePast = (dateString) => {
  if (!dateString) return false;
  
  try {
    const date = new Date(dateString).getTime();
    const now = new Date().getTime();
    
    return date < now;
  } catch (error) {
    console.error('Error checking if date is past:', error);
    return false;
  }
};
